import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { buildPetModel, PET_VOXEL, type PetModel, type PetPartModel } from '@vm/content';
import { PetState } from '@vm/shared';
import { buildModelGeometry } from './voxelModel';

const TAU = Math.PI * 2;

/** Merged skinned geometry of a pet: every voxel is bound to its part's bone (0 = body). */
export interface PetMesh {
  model: PetModel;
  lit: THREE.BufferGeometry | null;
  glow: THREE.BufferGeometry | null;
  /** Bone index per grid cell (0 = body, i + 1 = model.parts[i]). */
  owner: Uint8Array;
}

function glowFlags(model: PetModel): Uint8Array {
  const flags = new Uint8Array(256);
  for (const e of model.emissive) flags[e] = 1;
  return flags;
}

/** Tags every vertex with a single bone (skin index = bone, weight 1). */
function addSkin(g: THREE.BufferGeometry, bone: number): void {
  const n = g.getAttribute('position').count;
  const idx = new Uint16Array(n * 4);
  const w = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    idx[i * 4] = bone;
    w[i * 4] = 1;
  }
  g.setAttribute('skinIndex', new THREE.BufferAttribute(idx, 4));
  g.setAttribute('skinWeight', new THREE.BufferAttribute(w, 4));
}

const ownerCache = new WeakMap<Uint8Array, Uint8Array>();

/** Bone index per cell for a model (cached per shared grid). */
export function petBoneOwners(model: PetModel): Uint8Array {
  let owner = ownerCache.get(model.grid);
  if (owner) return owner;
  owner = new Uint8Array(model.grid.length);
  model.parts.forEach((p, k) => {
    const g = p.grid;
    for (let i = 0; i < g.length; i++) if (g[i]) owner![i] = k + 1;
  });
  ownerCache.set(model.grid, owner);
  return owner;
}

/**
 * Meshes a pet into two skinned geometries (lit + glow). `present` masks voxels that were chipped
 * away (null = the complete model). Positions are meters around the model center (bind pose).
 */
export function buildPetMesh(model: PetModel, present: Uint8Array | null = null): PetMesh {
  const { sx, sy, sz, grid } = model;
  const owner = petBoneOwners(model);
  const flags = glowFlags(model);
  const lit: THREE.BufferGeometry[] = [];
  const glow: THREE.BufferGeometry[] = [];
  const bones = model.parts.length + 1;
  const cells = new Uint8Array(grid.length);
  for (let bone = 0; bone < bones; bone++) {
    let any = false;
    for (let i = 0; i < grid.length; i++) {
      const keep = grid[i] !== 0 && owner[i] === bone && (!present || present[i] !== 0);
      cells[i] = keep ? grid[i] : 0;
      if (keep) any = true;
    }
    if (!any) continue;
    const geo = buildModelGeometry(cells, sx, sy, sz, model.palette, flags, PET_VOXEL, model.center);
    if (geo.lit) {
      addSkin(geo.lit, bone);
      lit.push(geo.lit);
    }
    if (geo.glow) {
      addSkin(geo.glow, bone);
      glow.push(geo.glow);
    }
  }
  const merge = (list: THREE.BufferGeometry[]): THREE.BufferGeometry | null => {
    if (!list.length) return null;
    if (list.length === 1) return list[0];
    const m = mergeGeometries(list, false);
    for (const g of list) g.dispose();
    return m;
  };
  return { model, lit: merge(lit), glow: merge(glow), owner };
}

const meshCache = new WeakMap<PetModel, PetMesh>();

/** The complete (unchipped) mesh of a pet in a style and team tint; cached and shared, never disposed. */
export function petMesh(pet: string | number, styleId?: string, team = -1): PetMesh | null {
  const model = buildPetModel(pet, styleId, team);
  if (!model) return null;
  let hit = meshCache.get(model);
  if (!hit) {
    hit = buildPetMesh(model);
    meshCache.set(model, hit);
  }
  return hit;
}

interface PartAnim {
  bone: THREE.Bone;
  part: PetPartModel;
  phase: number;
  angle: number;
  /** Smoothed rate multiplier (state changes ease in). */
  mul: number;
}

/**
 * A pet as a skinned voxel model: a body bone plus one bone per animated part, rendered as at most
 * two meshes (lit + glow) that share one skeleton. The caller places `root` (position = body
 * center, rotation.y = yaw) and calls animate() every frame.
 */
export class PetRig {
  readonly root = new THREE.Group();
  /** Body bone: bob, squash and tilt live here (parts follow it). */
  readonly body = new THREE.Bone();
  readonly model: PetModel;
  lit: THREE.SkinnedMesh | null = null;
  glow: THREE.SkinnedMesh | null = null;
  /** Jaw opening 0..1 (attackers). */
  jaw = 0;
  /** Extra spin/flap speed multiplier on top of the state (e.g. a drill spinning up). */
  boost = 1;
  private readonly skeleton: THREE.Skeleton;
  private readonly anims: PartAnim[] = [];
  private mesh: PetMesh;

  constructor(mesh: PetMesh, litMat: THREE.Material, glowMat: THREE.Material) {
    this.mesh = mesh;
    const m = mesh.model;
    this.model = m;
    const bones: THREE.Bone[] = [this.body];
    this.root.add(this.body);
    for (const p of m.parts) {
      const bone = new THREE.Bone();
      bone.position.set((p.pivot[0] - m.center[0]) * PET_VOXEL, (p.pivot[1] - m.center[1]) * PET_VOXEL, (p.pivot[2] - m.center[2]) * PET_VOXEL);
      this.body.add(bone);
      bones.push(bone);
      this.anims.push({ bone, part: p, phase: p.phase ?? 0, angle: (p.phase ?? 0) * TAU, mul: 1 });
    }
    // Bind in the rest pose with the root at the origin.
    this.root.updateMatrixWorld(true);
    this.skeleton = new THREE.Skeleton(bones);
    const bounds = new THREE.Sphere(new THREE.Vector3(), m.radius + 0.25);
    const make = (geo: THREE.BufferGeometry | null, mat: THREE.Material): THREE.SkinnedMesh | null => {
      if (!geo) return null;
      const s = new THREE.SkinnedMesh(geo, mat);
      s.boundingSphere = bounds;
      this.root.add(s);
      s.updateMatrixWorld(true);
      s.bind(this.skeleton, s.matrixWorld);
      return s;
    };
    this.lit = make(mesh.lit, litMat);
    this.glow = make(mesh.glow, glowMat);
  }

  /** The mesh currently shown (shared cache entry or a chipped copy). */
  get current(): PetMesh {
    return this.mesh;
  }

  /** Swaps in re-meshed geometry of the same model (chipping/restoring). */
  setMesh(mesh: PetMesh): void {
    this.mesh = mesh;
    if (this.lit) {
      if (mesh.lit) this.lit.geometry = mesh.lit;
      this.lit.visible = !!mesh.lit;
    }
    if (this.glow) {
      if (mesh.glow) this.glow.geometry = mesh.glow;
      this.glow.visible = !!mesh.glow;
    }
  }

  /**
   * Advances part animations. `state` is a PetState; work speeds wings/rotors up, active uses
   * each part's `active` multiplier.
   */
  animate(dt: number, state: number): void {
    const k = 1 - Math.exp(-dt * 5);
    for (const a of this.anims) {
      const p = a.part;
      const target = (state === PetState.Work ? (p.work ?? 2) : state === PetState.Active ? (p.active ?? 1.4) : state === PetState.Return ? 1.4 : 1) * this.boost;
      a.mul += (target - a.mul) * k;
      const sign = p.sign ?? 1;
      let angle: number;
      switch (p.kind) {
        case 'flap':
          a.phase = (a.phase + dt * p.rate * a.mul) % 1;
          angle = sign * ((p.offset ?? 0) + p.amp * Math.sin(a.phase * TAU));
          break;
        case 'sway':
          a.phase = (a.phase + dt * p.rate * a.mul) % 1;
          angle = p.amp * Math.sin(a.phase * TAU) + Math.sin(a.phase * TAU * 2.3 + 1.1) * p.amp * 0.25;
          break;
        case 'spin':
          a.angle = (a.angle + dt * p.rate * a.mul * TAU * sign) % TAU;
          angle = a.angle;
          break;
        default:
          angle = sign * p.amp * this.jaw;
      }
      a.bone.rotation[p.axis] = angle;
    }
  }

  /** Removes the meshes (shared geometry is not disposed; chipped copies are the caller's). */
  dispose(): void {
    if (this.lit) this.root.remove(this.lit);
    if (this.glow) this.root.remove(this.glow);
    this.lit = this.glow = null;
    this.skeleton.dispose();
  }
}
